// @ts-strict-ignore
import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { LMUniqueIdService } from '@utils/lm-unique-id.service';

@Component({
  selector: 'lm-compute-builder-row',
  templateUrl: './lm-compute-builder-row.component.html',
  styleUrls: ['./lm-compute-builder-row.component.scss']
})
export class LMComputeBuilderRowComponent implements OnInit {

  @Input() isFirst: boolean;
  @Input() isLast: boolean;
  @Input() computeRow: LMComputeRow;
  @Input() businessClass: string;
  @Input() includeAuditFields: boolean;
  @Input() lookupUrl: string;
  @Input() index: number;
  @Input() fieldSearchUrl: string;
  @Input() lplValidationUrl: string;

  @Output() onRemoveRowEvent = new EventEmitter<LMComputeBuilderRemoveRowEvent>();
  @Output() onComputeRowUpdate = new EventEmitter<LMComputeRowUpdateEvent>();
  @Output() onAddNewRowEvent = new EventEmitter<LMComputeBuilderAddRowEvent>();

  operators: Array<any>;
  functions: Array<any>;
  types: Array<any>;
  literalId: string;
  functionId: string;
  operatorId: string;
  typeId: string;

  literalAutomationId: string;
  functionAutomationId: SohoAttributesArray;
  operatorAutomationId: SohoAttributesArray;
  typeAutomationId: SohoAttributesArray;
  deleteAutomationId: string;

  constructor(private changeDetectorRef: ChangeDetectorRef,
    private lmUniqueIdService: LMUniqueIdService) { }

  ngOnInit(): void {

    this.literalId = this.lmUniqueIdService.uniqueId(['literal', this.index.toString()]);
    this.functionId = this.lmUniqueIdService.uniqueId(['function', this.index.toString()]);
    this.operatorId = this.lmUniqueIdService.uniqueId(['operator', this.index.toString()]);
    this.typeId = this.lmUniqueIdService.uniqueId(['type', this.index.toString()]);

    this.literalAutomationId = `lm-compute-builder-row-literal-${this.index}`;
    this.functionAutomationId = [{ name: 'data-automation-id', value: `lm-compute-builder-row-function-${this.index}` }];
    this.operatorAutomationId = [{ name: 'data-automation-id', value: `lm-compute-builder-row-operator-${this.index}` }];
    this.typeAutomationId = [{ name: 'data-automation-id', value: `lm-compute-builder-row-type-${this.index}` }];
    this.deleteAutomationId = `lm-compute-builder-row-delete-${this.index}`;

    this.types = [];
    this.types.push({ value: 'field', label: 'Field' });
    this.types.push({ value: 'string', label: 'String' });
    this.types.push({ value: 'number', label: 'Number' });

    this.operators = [];
    this.operators.push({ value: '', label: '' });
    this.operators.push({ value: '+', label: '+' });
    this.operators.push({ value: '-', label: '-' });
    this.operators.push({ value: '*', label: '*' });
    this.operators.push({ value: '/', label: '/' });
    this.operators.push({ value: '^', label: '^' });
    this.operators.push({ value: '%', label: '%' });

    this.functions = [];
    this.functions.push({ value: '', label: '', position: 'noop'});
    this.functions.push({ value: 'weighted average', label: 'weighted average', position: 'prepend'});
    this.functions.push({ value: 'date first changed', label: 'date first changed', position: 'prepend'});
    this.functions.push({ value: 'date last changed', label: 'date last changed', position: 'prepend'});
    this.functions.push({ value: 'date next changed', label: 'date next changed', position: 'prepend'});
    this.functions.push({ value: 'days was', label: 'days was', position: 'prepend'});
    this.functions.push({ value: 'floor', label: 'floor', position: 'prepend'});
    this.functions.push({ value: 'ceiling', label: 'ceiling', position: 'append'});
    this.functions.push({ value: 'decimals', label: 'decimals', position: 'append'});
    this.functions.push({ value: 'size', label: 'size', position: 'append'});
  }

  onRemoveButtonEvent() {
    this.computeRow.index = this.index;
    this.onRemoveRowEvent.emit({ computeRow: this.computeRow });
  }

  handleFieldLookUpEvent() {
    this.setupFunctionSelection();
    this.changeDetectorRef.detectChanges();
  }

  handleFieldChange() {
    if (this.computeRow.function) {
      const element = this.functions.find(operation => operation.value === this.computeRow.function);
      this.computeRow.position = element.position;
    }
    this.onComputeRowUpdate.emit({computeRow: this.computeRow});
  }

  setupFunctionSelection() {
    if (this.includeAuditFields) {
      this.setupAuditFunctionSelection();
    } else {
      this.setupNonAuditFunctionSelection();
    }
  }

  private setupAuditFunctionSelection() {
    this.functions = [];

    // this should use the field pricker to select the field Operator, but then have the function be the type operator  DPB
    // should be based on the landmark field type.
    this.functions.push({ value: '', label: '', position: 'noop'});
    this.functions.push({ value: 'decimals', label: 'decimals', position: 'append'});
    this.functions.push({ value: 'size', label: 'size', position: 'append'});

    if (this.computeRow.field.type === 'DATE') {
      this.functions.push({ value: 'difference', label: 'difference', position: 'custom'});
      this.functions.push({ value: 'year', label: 'year', position: 'append'});
      this.functions.push({ value: 'month', label: 'month', position: 'append'});
      this.functions.push({ value: 'day', label: 'day', position: 'append'});
      this.functions.push({ value: 'date', label: 'date', position: 'append'});
      this.functions.push({ value: 'time', label: 'time', position: 'append'});
      this.functions.push({ value: 'week day', label: 'week day', position: 'append'});
      this.functions.push({ value: 'year day', label: 'year day', position: 'append'});
      this.functions.push({ value: 'week', label: 'week', position: 'append'});
      this.functions.push({ value: 'hours', label: 'hours', position: 'append'});
      this.functions.push({ value: 'minutes', label: 'minutes', position: 'append'});
      this.functions.push({ value: 'seconds', label: 'seconds', position: 'append'});
      this.functions.push({ value: 'as years', label: 'as years', position: 'append'});
      this.functions.push({ value: 'as months', label: 'as months', position: 'append'});
      this.functions.push({ value: 'as days', label: 'as days', position: 'append'});
      this.functions.push({ value: 'days in month', label: 'days in month', position: 'append'});
      this.functions.push({ value: 'days in year', label: 'days in year', position: 'append'});
      this.functions.push({ value: 'anniversary', label: 'anniversary', position: 'append'});
      this.functions.push({ value: 'period', label: 'period', position: 'append'});
    }

    if (this.computeRow.field.type === 'NUMBER') {
      this.functions.push({ value: 'difference', label: 'difference', position: 'custom'});
      this.functions.push({ value: 'percent-changed', label: 'percent changed', position: 'custom'});
    }
    this.handleFieldChange();
  }

  private setupNonAuditFunctionSelection() {
    this.functions = [];

    // this should use the field pricker to select the field Operator, but then have the function be the type operator  DPB
    // should be based on the landmark field type.
    this.functions.push({ value: '', label: '', position: 'noop'});
    this.functions.push({ value: 'weighted average', label: 'weighted average', position: 'append'});
    this.functions.push({ value: 'date first changed', label: 'date first changed', position: 'append'});
    this.functions.push({ value: 'date last changed', label: 'date last changed', position: 'append'});
    this.functions.push({ value: 'date next changed', label: 'date next changed', position: 'append'});
    this.functions.push({ value: 'days was', label: 'days was', position: 'append'});
    this.functions.push({ value: 'floor', label: 'floor', position: 'prepend'});
    this.functions.push({ value: 'ceiling', label: 'ceiling', position: 'prepend'});
    this.functions.push({ value: 'decimals', label: 'decimals', position: 'append'});
    this.functions.push({ value: 'size', label: 'size', position: 'append'});

    if (this.computeRow.field.type === 'DATE') {
      this.functions.push({ value: 'year', label: 'year', position: 'append'});
      this.functions.push({ value: 'month', label: 'month', position: 'append'});
      this.functions.push({ value: 'day', label: 'day', position: 'append'});
      this.functions.push({ value: 'date', label: 'date', position: 'append'});
      this.functions.push({ value: 'time', label: 'time', position: 'append'});
      this.functions.push({ value: 'week day', label: 'week day', position: 'append'});
      this.functions.push({ value: 'year day', label: 'year day', position: 'append'});
      this.functions.push({ value: 'week', label: 'week', position: 'append'});
      this.functions.push({ value: 'hours', label: 'hours', position: 'append'});
      this.functions.push({ value: 'minutes', label: 'minutes', position: 'append'});
      this.functions.push({ value: 'seconds', label: 'seconds', position: 'append'});
      this.functions.push({ value: 'as years', label: 'as years', position: 'append'});
      this.functions.push({ value: 'as months', label: 'as months', position: 'append'});
      this.functions.push({ value: 'as days', label: 'as days', position: 'append'});
      this.functions.push({ value: 'days in month', label: 'days in month', position: 'append'});
      this.functions.push({ value: 'days in year', label: 'days in year', position: 'append'});
      this.functions.push({ value: 'anniversary', label: 'anniversary', position: 'append'});
      this.functions.push({ value: 'period', label: 'period', position: 'append'});
    }

    if (this.computeRow.field.type === 'RELATION') {
      this.functions.push({ value: 'sum', label: 'sum', position: 'append'});
      this.functions.push({ value: 'avg', label: 'avg', position: 'append'});
      this.functions.push({ value: 'instance count of', label: 'instance count of', position: 'prepend'});
      this.functions.push({ value: 'first', label: 'first', position: 'append'});
      this.functions.push({ value: 'last', label: 'last', position: 'append'});
    }
    this.handleFieldChange();
  }

  onOperatorChange() {
    if (this.isLast && this.computeRow.operator !== '') {
      this.onAddNewRowEvent.emit({ computeRow: this.computeRow });
    }
    this.handleFieldChange();
  }

  clearField() {
    this.computeRow.field.text = '';
    this.computeRow.field.type = 'TEXT';
    this.computeRow.function = '';
    this.handleFieldChange();
  }
}

